import FormLayout from '../models/FormLayout';
import {
    formGroupReduxPrefix,
    getSortedSteps,
    getStepLayoutSequence,
    getSortedFieldsInGroup,
} from './formLayoutGroupUtils';

function is_empty_step_value(v) {
    if (v === undefined || v === null) {
        return true;
    }
    if (typeof v === 'string') {
        return v.trim() === '';
    }
    if (Array.isArray(v)) {
        return v.length === 0;
    }
    if (typeof v === 'object' && !Array.isArray(v)) {
        return Object.keys(v).length === 0;
    }
    return false;
}

/**
 * Redux-form paths of required fields that are empty on the given wizard step.
 * Paths match Field `name` values (root id, `grp_<id>.<fieldId>`, `grp_<id>[i].<fieldId>`).
 *
 * @param {import('../models/FormLayout').default|object} formLayout
 * @param {number} stepIndex 0-based index in sorted steps
 * @param {Record<string, unknown>} form_values state.form.formRenderer.values
 * @returns {string[]}
 */
export function getInvalidStepFieldPaths(formLayout, stepIndex, form_values) {
    const layout = formLayout instanceof FormLayout ? formLayout : new FormLayout(formLayout || { fields: [], groups: [] });
    const steps = getSortedSteps(layout.steps || []);
    const step = steps[stepIndex];
    if (!step) {
        return [];
    }
    const seq = getStepLayoutSequence(layout.fields, layout.groups, steps, step.id);
    const fields = layout.fields || [];
    const vals = form_values || {};
    const invalid = [];

    for (const item of seq) {
        if (item.kind === 'field') {
            const field = FormLayout.createField(item.ref) || item.ref;
            if (FormLayout.isDisplayOnlyField(field.type) || !field.required) {
                continue;
            }
            if (is_empty_step_value(vals[field.id])) {
                invalid.push(String(field.id));
            }
        } else {
            const group = item.ref;
            const members = getSortedFieldsInGroup(fields, group.id)
                .filter((f) => !FormLayout.isDisplayOnlyField(f.type) && f.required);
            if (members.length === 0) {
                continue;
            }
            const prefix = formGroupReduxPrefix(group.id);
            if (!group.multiple) {
                const row = vals[prefix];
                if (!row || typeof row !== 'object' || Array.isArray(row)) {
                    members.forEach((f) => invalid.push(`${prefix}.${f.id}`));
                    continue;
                }
                for (const f of members) {
                    if (is_empty_step_value(row[f.id])) {
                        invalid.push(`${prefix}.${f.id}`);
                    }
                }
            } else {
                const rows = Array.isArray(vals[prefix]) ? vals[prefix] : [];
                const minRows = Math.max(1, Number(group.minItems ?? group.min_items) || 1);
                for (let i = 0; i < minRows; i += 1) {
                    const row = rows[i];
                    if (!row || typeof row !== 'object') {
                        members.forEach((f) => invalid.push(`${prefix}[${i}].${f.id}`));
                        continue;
                    }
                    for (const f of members) {
                        if (is_empty_step_value(row[f.id])) {
                            invalid.push(`${prefix}[${i}].${f.id}`);
                        }
                    }
                }
            }
        }
    }
    return invalid;
}

/**
 * Whether all required fields in the given wizard step have non-empty values in redux-form state.
 *
 * @param {import('../models/FormLayout').default|object} formLayout
 * @param {number} stepIndex 0-based index in sorted steps
 * @param {Record<string, unknown>} form_values state.form.formRenderer.values
 */
export function isCurrentStepValid(formLayout, stepIndex, form_values) {
    return getInvalidStepFieldPaths(formLayout, stepIndex, form_values).length === 0;
}

/**
 * @param {import('../models/FormLayoutStep').default|object} step
 * @param {string} formDefault
 */
export function resolveStepTransitionClass(step, formDefault) {
    const t = step?.transition ?? step?.Transition;
    const resolved = t === 'inherit' || t === undefined || t === null ? (formDefault || 'fade') : t;
    if (resolved === 'none') {
        return '';
    }
    return `form-step-anim--${resolved}`;
}
